import { html, css } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { PageView } from '@operato/shell'
import { client } from '@operato/graphql'
import gql from 'graphql-tag'
import { navigate } from '@operato/shell'

/**
 * Workflow List Page
 *
 * Displays all labeling workflows with CRUD operations
 */
@customElement('labeling-workflow-list-page')
export class LabelingWorkflowListPage extends PageView {
  static styles = [
    css`
      :host {
        display: flex;
        flex-direction: column;
        padding: 20px;
        background-color: var(--md-sys-color-surface);
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
      }

      h1 {
        font-size: 24px;
        margin: 0;
        color: var(--md-sys-color-on-surface);
      }

      .toolbar {
        display: flex;
        gap: 10px;
      }

      button {
        padding: 10px 20px;
        background-color: var(--md-sys-color-primary);
        color: var(--md-sys-color-on-primary);
        border: none;
        border-radius: 4px;
        cursor: pointer;
      }

      button:hover {
        opacity: 0.9;
      }

      button.secondary {
        background-color: var(--md-sys-color-secondary);
        color: var(--md-sys-color-on-secondary);
      }

      .workflow-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
        gap: 20px;
      }

      .workflow-card {
        background-color: var(--md-sys-color-surface-variant);
        border-radius: 8px;
        padding: 20px;
        cursor: pointer;
        transition:
          transform 0.2s,
          box-shadow 0.2s;
        border: 1px solid var(--md-sys-color-outline);
      }

      .workflow-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .workflow-header {
        display: flex;
        justify-content: space-between;
        align-items: start;
        margin-bottom: 10px;
      }

      .workflow-name {
        font-size: 18px;
        font-weight: bold;
        margin-bottom: 5px;
        color: var(--md-sys-color-on-surface);
      }

      .workflow-status {
        display: inline-block;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
      }

      .status-active {
        background-color: #4caf50;
        color: white;
      }

      .status-draft {
        background-color: #9e9e9e;
        color: white;
      }

      .status-paused {
        background-color: #ff9800;
        color: white;
      }

      .status-completed {
        background-color: #2196f3;
        color: white;
      }

      .status-failed {
        background-color: #f44336;
        color: white;
      }

      .workflow-description {
        font-size: 14px;
        color: var(--md-sys-color-on-surface-variant);
        margin-bottom: 10px;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      .workflow-info {
        font-size: 12px;
        color: var(--md-sys-color-on-surface-variant);
        margin-bottom: 5px;
      }

      .workflow-steps {
        font-size: 12px;
        color: var(--md-sys-color-on-surface-variant);
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px solid var(--md-sys-color-outline);
      }

      .workflow-actions {
        display: flex;
        gap: 5px;
        margin-top: 15px;
      }

      .workflow-actions button {
        flex: 1;
        padding: 8px;
        font-size: 12px;
      }

      .loading {
        text-align: center;
        padding: 40px;
        color: var(--md-sys-color-on-surface-variant);
      }

      .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: var(--md-sys-color-on-surface-variant);
      }

      .empty-state h2 {
        font-size: 20px;
        margin-bottom: 10px;
      }

      .empty-state p {
        font-size: 14px;
        margin-bottom: 20px;
      }

      .filter-bar {
        display: flex;
        gap: 10px;
        margin-bottom: 20px;
        align-items: center;
      }

      .filter-bar select,
      .filter-bar input {
        padding: 8px 12px;
        border: 1px solid var(--md-sys-color-outline);
        border-radius: 4px;
        background-color: var(--md-sys-color-surface);
        color: var(--md-sys-color-on-surface);
      }

      .filter-bar label {
        font-size: 14px;
        color: var(--md-sys-color-on-surface-variant);
      }
    `
  ]

  @state() workflows: any[] = []
  @state() loading: boolean = true
  @state() filterStatus: string = 'all'
  @state() filterProjectId: string = ''

  async firstUpdated() {
    await this.loadWorkflows()
  }

  async loadWorkflows() {
    this.loading = true
    try {
      const response = await client.query({
        query: gql`
          query GetWorkflows($projectId: Int) {
            labelingWorkflows(projectId: $projectId) {
              items {
                id
                name
                description
                projectId
                status
                triggerType
                steps {
                  name
                  type
                }
                createdAt
                lastExecutedAt
              }
            }
          }
        `,
        variables: this.filterProjectId ? { projectId: parseInt(this.filterProjectId) } : {}
      })

      this.workflows = response.data.labelingWorkflows.items || []
    } catch (error) {
      console.error('Failed to load workflows:', error)
      alert('Failed to load workflows')
    } finally {
      this.loading = false
    }
  }

  createWorkflow() {
    navigate('workflow-builder')
  }

  editWorkflow(workflowId: string, event: Event) {
    event.stopPropagation()
    navigate(`workflow-builder/${workflowId}`)
  }

  async executeWorkflow(workflowId: string, event: Event) {
    event.stopPropagation()

    if (!confirm('Execute this workflow?')) return

    try {
      const response = await client.mutate({
        mutation: gql`
          mutation ExecuteWorkflow($workflowId: String!) {
            executeLabelingWorkflow(input: { workflowId: $workflowId }) {
              executionId
              status
              summary
            }
          }
        `,
        variables: { workflowId }
      })

      const result = response.data.executeLabelingWorkflow
      alert(`Workflow execution started!\nExecution ID: ${result.executionId}`)
      await this.loadWorkflows()
    } catch (error) {
      console.error('Failed to execute workflow:', error)
      alert('Failed to execute workflow')
    }
  }

  async pauseWorkflow(workflowId: string, event: Event) {
    event.stopPropagation()

    if (!confirm('Pause this workflow?')) return

    try {
      await client.mutate({
        mutation: gql`
          mutation PauseWorkflow($workflowId: String!) {
            pauseLabelingWorkflow(workflowId: $workflowId) {
              id
              status
            }
          }
        `,
        variables: { workflowId }
      })

      alert('Workflow paused successfully')
      await this.loadWorkflows()
    } catch (error) {
      console.error('Failed to pause workflow:', error)
      alert('Failed to pause workflow')
    }
  }

  async resumeWorkflow(workflowId: string, event: Event) {
    event.stopPropagation()

    if (!confirm('Resume this workflow?')) return

    try {
      await client.mutate({
        mutation: gql`
          mutation ResumeWorkflow($workflowId: String!) {
            resumeLabelingWorkflow(workflowId: $workflowId) {
              id
              status
            }
          }
        `,
        variables: { workflowId }
      })

      alert('Workflow resumed successfully')
      await this.loadWorkflows()
    } catch (error) {
      console.error('Failed to resume workflow:', error)
      alert('Failed to resume workflow')
    }
  }

  async deleteWorkflow(workflowId: string, event: Event) {
    event.stopPropagation()

    if (!confirm('Are you sure you want to delete this workflow? This action cannot be undone.')) return

    try {
      await client.mutate({
        mutation: gql`
          mutation DeleteWorkflow($workflowId: String!) {
            deleteLabelingWorkflow(workflowId: $workflowId)
          }
        `,
        variables: { workflowId }
      })

      alert('Workflow deleted successfully')
      await this.loadWorkflows()
    } catch (error) {
      console.error('Failed to delete workflow:', error)
      alert('Failed to delete workflow')
    }
  }

  getStatusClass(status: string) {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'status-active'
      case 'draft':
        return 'status-draft'
      case 'paused':
        return 'status-paused'
      case 'completed':
        return 'status-completed'
      case 'failed':
        return 'status-failed'
      default:
        return ''
    }
  }

  get filteredWorkflows() {
    return this.workflows.filter(workflow => {
      if (this.filterStatus !== 'all' && workflow.status !== this.filterStatus) {
        return false
      }
      return true
    })
  }

  render() {
    if (this.loading) {
      return html`<div class="loading">Loading workflows...</div>`
    }

    return html`
      <div class="header">
        <h1>Labeling Workflows</h1>
        <div class="toolbar">
          <button @click=${this.createWorkflow}>Create Workflow</button>
          <button class="secondary" @click=${this.loadWorkflows}>Refresh</button>
        </div>
      </div>

      <div class="filter-bar">
        <label>Status:</label>
        <select
          .value=${this.filterStatus}
          @change=${(e: any) => {
            this.filterStatus = e.target.value
            this.requestUpdate()
          }}
        >
          <option value="all">All</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>

        <label>Project ID:</label>
        <input
          type="number"
          .value=${this.filterProjectId}
          @input=${(e: any) => (this.filterProjectId = e.target.value)}
          placeholder="Filter by project"
        />
        <button class="secondary" @click=${this.loadWorkflows}>Apply Filter</button>
      </div>

      ${this.filteredWorkflows.length === 0
        ? html`
            <div class="empty-state">
              <h2>No workflows found</h2>
              <p>Create your first labeling workflow to get started</p>
              <button @click=${this.createWorkflow}>Create Workflow</button>
            </div>
          `
        : html`
            <div class="workflow-list">
              ${this.filteredWorkflows.map(
                workflow => html`
                  <div class="workflow-card">
                    <div class="workflow-header">
                      <div class="workflow-name">${workflow.name}</div>
                      <span class="workflow-status ${this.getStatusClass(workflow.status)}">${workflow.status}</span>
                    </div>

                    <div class="workflow-description">${workflow.description || 'No description'}</div>

                    <div class="workflow-info">Project ID: ${workflow.projectId}</div>
                    <div class="workflow-info">Trigger: ${workflow.triggerType}</div>
                    <div class="workflow-info">Created: ${new Date(workflow.createdAt).toLocaleDateString()}</div>
                    ${workflow.lastExecutedAt
                      ? html`<div class="workflow-info">
                          Last executed: ${new Date(workflow.lastExecutedAt).toLocaleDateString()}
                        </div>`
                      : ''}

                    <div class="workflow-steps">${workflow.steps.length} steps configured</div>

                    <div class="workflow-actions">
                      <button @click=${(e: Event) => this.editWorkflow(workflow.id, e)}>Edit</button>
                      <button @click=${(e: Event) => this.executeWorkflow(workflow.id, e)}>Execute</button>
                      ${workflow.status === 'active'
                        ? html`<button class="secondary" @click=${(e: Event) => this.pauseWorkflow(workflow.id, e)}>
                            Pause
                          </button>`
                        : workflow.status === 'paused'
                          ? html`<button @click=${(e: Event) => this.resumeWorkflow(workflow.id, e)}>Resume</button>`
                          : ''}
                      <button class="secondary" @click=${(e: Event) => this.deleteWorkflow(workflow.id, e)}>
                        Delete
                      </button>
                    </div>
                  </div>
                `
              )}
            </div>
          `}
    `
  }
}
